import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TableCell,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Folder,
  InsertDriveFileOutlined,
  SubdirectoryArrowRight,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  ENVIRONMENTS_OPTIONS,
  ROLES_OPTIONS,
  tableColumnWidths,
} from '../utils/constants';
import React from 'react';
import {
  type NavItemSkeleton,
  type INavigationConfigDBItem,
} from '../types/types';
import {
  normalizeNavModule,
  toDbNavModule,
  type AppModuleKey,
} from 'components/Route/utils';
import { useTranslation } from 'react-i18next';

export const ConfigRow = React.memo(
  ({
    node,
    config,
    onUpdate,
  }: {
    node: NavItemSkeleton;
    config: INavigationConfigDBItem;
    onUpdate: (
      id: string,
      field: keyof INavigationConfigDBItem,
      value: any
    ) => void;
  }) => {
    const { t } = useTranslation();
    const isParent = node.depth === 0;
    const isDisabled = !config.webEnabled;
    const moduleValue =
      normalizeNavModule(config.module) ?? ('common' as const);

    return (
      <TableRow
        sx={{
          opacity: isDisabled ? 0.4 : 1,
          transition: 'opacity 0.2s',
        }}
      >
        <TableCell
          sx={{
            width: tableColumnWidths.screen,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: node.depth * 16, flexShrink: 0 }} />
            <Box sx={{ mr: 1, display: 'flex' }}>
              {node.icon ? (
                node.icon
              ) : node.depth > 0 ? (
                <SubdirectoryArrowRight fontSize="small" />
              ) : node.hasChildren ? (
                <Folder fontSize="small" />
              ) : (
                <InsertDriveFileOutlined fontSize="small" />
              )}
            </Box>
            <Box>
              <Tooltip title={node.id} arrow>
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: isParent ? 600 : 500,
                    color: 'text.primary',
                  }}
                  noWrap={true}
                  maxWidth={'250px'}
                >
                  {node.id}
                </Typography>
              </Tooltip>
              <Tooltip title={`/${node.path}`} arrow>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                  noWrap={true}
                  maxWidth={'250px'}
                >
                  /{node.path}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            textAlign: 'center',
            width: tableColumnWidths.webEnabled,
          }}
        >
          <Switch
            checked={config.webEnabled}
            onChange={(e) => {
              onUpdate(node.id, 'webEnabled', e.target.checked);
            }}
            color="primary"
            size="small"
          />
        </TableCell>

        <TableCell
          sx={{
            textAlign: 'center',
            width: tableColumnWidths.module,
          }}
        >
          {isParent ? (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={moduleValue}
              onChange={(_, nextValue: AppModuleKey | 'common' | null) => {
                if (!nextValue) return;
                onUpdate(
                  node.id,
                  'module',
                  nextValue === 'common' ? '' : toDbNavModule(nextValue)
                );
              }}
              disabled={isDisabled}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  px: 1,
                  py: 0.25,
                },
              }}
            >
              <ToggleButton value="common">Common</ToggleButton>
              <ToggleButton value="machines">Machines</ToggleButton>
              <ToggleButton value="parts">{t('parts')}</ToggleButton>
              <ToggleButton value="tires">Tires</ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>

        <TableCell
          sx={{
            width: tableColumnWidths.displayLabel,
          }}
        >
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            disabled={isDisabled}
            value={config.label}
            onChange={(e) => {
              onUpdate(node.id, 'label', e.target.value);
            }}
            inputProps={{
              style: {
                fontSize: '12px',
              },
            }}
          />
        </TableCell>

        <TableCell
          sx={{
            width: tableColumnWidths.allowedRoles,
          }}
        >
          <FormControl fullWidth size="small" disabled={isDisabled}>
            <Select
              multiple
              displayEmpty
              value={config.roles}
              onChange={(e) => {
                const value = e.target.value;
                if (Array.isArray(value) && value.length > 0) {
                  onUpdate(node.id, 'roles', e.target.value);
                }
              }}
              input={<OutlinedInput />}
              renderValue={(selected: any) => {
                if (selected.length === 0)
                  return (
                    <Typography variant="caption" color="textSecondary">
                      {t('all_roles')}
                    </Typography>
                  );
                return (
                  <Typography variant="caption">
                    {selected.length} selected
                  </Typography>
                );
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {ROLES_OPTIONS.map((r) => (
                <MenuItem key={r} value={r} dense>
                  <Checkbox checked={config.roles.includes(r)} size="small" />
                  <ListItemText primary={r} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>

        <TableCell
          sx={{
            width: tableColumnWidths.environments,
          }}
        >
          <FormControl fullWidth size="small" disabled={isDisabled}>
            <Select
              multiple
              displayEmpty
              value={config.environments}
              onChange={(e) => {
                const value = e.target.value;
                if (Array.isArray(value) && value.length > 0) {
                  onUpdate(node.id, 'environments', e.target.value);
                }
              }}
              input={<OutlinedInput />}
              renderValue={(selected: any) => {
                if (selected.length === 0)
                  return (
                    <Typography variant="caption" color="error">
                      None
                    </Typography>
                  );
                return (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {selected.map((v: string) => (
                      <Chip
                        key={v}
                        label={v.substring(0, 4).toUpperCase()}
                        size="small"
                        sx={{ height: 20, fontSize: '0.6rem' }}
                        color="info"
                      />
                    ))}
                  </Box>
                );
              }}
            >
              {ENVIRONMENTS_OPTIONS.map((env) => (
                <MenuItem key={env} value={env} dense>
                  <Checkbox
                    checked={config.environments.includes(env)}
                    size="small"
                  />
                  <ListItemText primary={env} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>

        <TableCell
          sx={{
            textAlign: 'center',
            width: '8%',
          }}
        >
          <Switch
            disabled={isDisabled}
            checked={config.showPro ?? false}
            onChange={(_, checked) => {
              onUpdate(node.id, 'showPro', checked);
            }}
            color="primary"
            size="small"
            inputProps={{ 'aria-label': t('show_pro') }}
          />
        </TableCell>

        <TableCell
          sx={{
            textAlign: 'center',
            width: tableColumnWidths.showInSidebar,
          }}
        >
          <IconButton
            disabled={isDisabled}
            onClick={() => {
              onUpdate(node.id, 'invisible', !config.invisible);
            }}
            size="small"
            sx={{
              color: config.invisible ? '#EF4444' : '#10B981',
              opacity: isDisabled ? 0.3 : 1,
            }}
          >
            {config.invisible ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </TableCell>
      </TableRow>
    );
  },
  (prev, next) => prev.config === next.config && prev.node.id === next.node.id
);
ConfigRow.displayName = 'Config row';
